import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Calendar, CheckCircle, Clock, AlertCircle, Link as LinkIcon, ChevronLeft, Globe } from 'lucide-react';
import MonthView from '../components/CalendarGrid/MonthView';
import { COMMON_TIMEZONES, tzLabel } from '../constants/timezones';
import styles from './VennBooking.module.css';

function postToParent(msg) {
  if (window.parent !== window) {
    window.parent.postMessage(msg, '*');
  }
}

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const POLL_INTERVAL_MS = 30_000;


function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatNextAvailable(isoString) {
  const date = new Date(isoString);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const sixDaysOut = new Date(today); sixDaysOut.setDate(today.getDate() + 6);
  const dateOnly = new Date(date); dateOnly.setHours(0, 0, 0, 0);
  const time = date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dateOnly.getTime() === today.getTime()) return `Today, ${time}`;
  if (dateOnly.getTime() === tomorrow.getTime()) return `Tomorrow, ${time}`;
  if (dateOnly <= sixDaysOut) return `${date.toLocaleDateString('en-GB', { weekday: 'short' })}, ${time}`;
  return `${date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${time}`;
}

// Calendar link helpers
function fmtGcal(iso) {
  return new Date(iso).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function googleCalendarUrl(title, start, end) {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmtGcal(start)}/${fmtGcal(end)}`,
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

function outlookUrl(title, start, end) {
  const params = new URLSearchParams({
    subject: title,
    startdt: new Date(start).toISOString(),
    enddt: new Date(end).toISOString(),
  });
  return `https://outlook.live.com/calendar/0/action/compose?${params}`;
}

function downloadICS(title, start, end) {
  const fmt = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const uid = `${Date.now()}-vennio@vennio.app`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vennio//Vennio//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${title.replace(/[,;\\]/g, '\\$&')}`,
    `DTSTAMP:${fmt(new Date().toISOString())}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'booking.ics';
  a.click();
  URL.revokeObjectURL(url);
}

export default function VennBooking() {
  const { shortCode } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1';
  const [loading, setLoading] = useState(true);
  const [vennLink, setVennLink] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Date object
  const [viewDate, setViewDate] = useState(new Date());   // calendar month
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [confirmedSlot, setConfirmedSlot] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedSlotRef = useRef(null);
  selectedSlotRef.current = selectedSlot;
  const isSubmittingRef = useRef(false);
  isSubmittingRef.current = isSubmitting;

  // Build availability map: YYYY-MM-DD → slot count
  const availabilityMap = useMemo(() => {
    const map = {};
    for (const slot of availableSlots) {
      const key = toDateKey(new Date(slot.start));
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [availableSlots]);

  // Slots for the currently selected date
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const key = toDateKey(selectedDate);
    return availableSlots.filter(s => toDateKey(new Date(s.start)) === key);
  }, [selectedDate, availableSlots]);

  // Nudge: soonest available slot
  const nextAvailableSlot = useMemo(() => {
    if (!availableSlots.length) return null;
    return availableSlots[0]; // API returns slots sorted ASC
  }, [availableSlots]);

  // Nudge: slot counts for this week and next (Mon–Sun, UK-first)
  const weekSummary = useMemo(() => {
    if (!availableSlots.length) return null;
    const now = new Date();
    const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 … Sun=6
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - dayOfWeek);
    thisWeekStart.setHours(0, 0, 0, 0);
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 7);
    const nextWeekEnd = new Date(thisWeekEnd);
    nextWeekEnd.setDate(thisWeekEnd.getDate() + 7);
    const thisWeekCount = availableSlots.filter(s => {
      const d = new Date(s.start);
      return d >= thisWeekStart && d < thisWeekEnd;
    }).length;
    const nextWeekCount = availableSlots.filter(s => {
      const d = new Date(s.start);
      return d >= thisWeekEnd && d < nextWeekEnd;
    }).length;
    if (thisWeekCount === 0 && nextWeekCount === 0) return null;
    return { thisWeekCount, nextWeekCount };
  }, [availableSlots]);

  const refreshAvailability = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/v1/venn-links/${shortCode}/availability`);
      if (!res.ok) return;
      const data = await res.json();
      const freshSlots = data.available_slots || [];
      setAvailableSlots(freshSlots);
      // Clear slot selection if the chosen slot was booked by someone else
      // (but not while we're actively submitting — the slot disappears because WE just booked it)
      const sel = selectedSlotRef.current;
      if (sel && !isSubmittingRef.current && !freshSlots.some(s => s.start === sel.start && s.end === sel.end)) {
        setSelectedSlot(null);
      }
    } catch { /* silent — polling failure is non-fatal */ }
  }, [shortCode]);

  // Poll availability every 30s and on tab focus
  useEffect(() => {
    const interval = setInterval(refreshAvailability, POLL_INTERVAL_MS);
    const onVisibility = () => { if (document.visibilityState === 'visible') refreshAvailability(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisibility); };
  }, [refreshAvailability]);

  useEffect(() => { loadVennLink(); }, [shortCode]);

  const loadVennLink = async () => {
    try {
      setLoading(true); setError(null);
      const linkResponse = await fetch(`${API_URL}/v1/venn-links/${shortCode}`);
      if (!linkResponse.ok) {
        if (linkResponse.status === 404) throw new Error('This booking link does not exist or has expired.');
        if (linkResponse.status === 403) {
          const data = await linkResponse.json();
          throw new Error(data.message || 'This booking link is no longer active.');
        }
        throw new Error('Failed to load booking link.');
      }
      const linkData = await linkResponse.json();
      setVennLink(linkData.venn_link);
      postToParent({ type: 'vennio:loaded', shortCode });

      const slotsResponse = await fetch(`${API_URL}/v1/venn-links/${shortCode}/availability`);
      if (!slotsResponse.ok) throw new Error('Failed to load available times.');
      const slotsData = await slotsResponse.json();
      const slots = slotsData.available_slots || [];
      setAvailableSlots(slots);

      // Advance calendar to the month of the earliest future slot if it's not this month
      if (slots.length > 0) {
        const firstSlotDate = new Date(slots[0].start);
        const now = new Date();
        if (
          firstSlotDate.getFullYear() !== now.getFullYear() ||
          firstSlotDate.getMonth() !== now.getMonth()
        ) {
          setViewDate(new Date(firstSlotDate.getFullYear(), firstSlotDate.getMonth(), 1));
        }
      }
    } catch (err) {
      console.error('Error loading venn link:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDaySelect = (date) => {
    const key = toDateKey(date);
    if (!availabilityMap[key]) return; // no slots on this day — ignore
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { setError('Please select a time slot'); return; }
    if (!customerName.trim() || !customerEmail.trim()) { setError('Please provide your name and email'); return; }
    try {
      setIsSubmitting(true); setError(null);
      const response = await fetch(`${API_URL}/v1/venn-links/${shortCode}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_email: customerEmail,
          start_time: selectedSlot.start,
          timezone,
          notes: notes.trim() || undefined
        })
      });
      if (!response.ok) {
        if (response.status === 409) {
          // Slot taken — race condition. Refresh and clear selection.
          await refreshAvailability();
          setSelectedSlot(null);
          throw new Error('That slot was just booked — please pick another time.');
        }
        if (response.status === 429) {
          throw new Error('This service is temporarily unavailable. Please contact the business directly.');
        }
        const data = await response.json();
        throw new Error(data.message || 'Failed to create booking');
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmedSlot({ start: selectedSlot.start, end: selectedSlot.end });
      postToParent({
        type: 'vennio:booking_confirmed',
        shortCode,
        start: selectedSlot.start,
        end: selectedSlot.end,
        customerEmail,
      });
      setBookingSuccess(true);
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (isoString) => new Date(isoString).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });

  const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true
  });

  const formatPrice = (amount, currency) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: (currency || 'gbp').toUpperCase()
    }).format(amount / 100);
  };

  const formatTimeRange = (start, end) => `${formatTime(start)} - ${formatTime(end)}`;

  const selectedDayLabel = selectedDate
    ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  const pageClass = [styles.page, isEmbed ? styles.pageEmbed : ''].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={pageClass}>
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} aria-label="Loading" />
          <p className={styles.loadingText}>Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (error && !vennLink) {
    return (
      <div className={pageClass}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconError}`}>
            <AlertCircle size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>Link Not Available</h1>
          <p className={styles.stateBody}>{error}</p>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    const eventTitle = vennLink?.title || 'Meeting';
    return (
      <div className={pageClass}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconSuccess}`}>
            <CheckCircle size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>Booking Confirmed!</h1>
          <p className={styles.stateBody}>Your meeting has been scheduled for:</p>
          <div className={styles.confirmedSlot}>
            <p className={styles.confirmedDate}>{formatDate(confirmedSlot.start)}</p>
            <p className={styles.confirmedTime}>{formatTimeRange(confirmedSlot.start, confirmedSlot.end)}</p>
          </div>
          <p className={styles.confirmationNote}>
            A confirmation email has been sent to <strong>{customerEmail}</strong>
          </p>
          <div className={styles.calendarLinks}>
            <p className={styles.calendarLinksLabel}>Add to your calendar:</p>
            <div className={styles.calendarLinksBtns}>
              <a
                href={googleCalendarUrl(eventTitle, confirmedSlot.start, confirmedSlot.end)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.calendarLinkBtn}
              >
                Google
              </a>
              <button
                type="button"
                onClick={() => downloadICS(eventTitle, confirmedSlot.start, confirmedSlot.end)}
                className={styles.calendarLinkBtn}
              >
                Apple
              </button>
              <a
                href={outlookUrl(eventTitle, confirmedSlot.start, confirmedSlot.end)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.calendarLinkBtn}
              >
                Outlook
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const businessName = vennLink?.business?.business_profile?.business_name ||
                       vennLink?.business?.full_name || 'Business';

  return (
    <div className={pageClass}>
      <div className={styles.inner}>
        {/* Header card */}
        <div className={styles.card}>
          <div className={styles.linkHeader}>
            <div className={styles.linkHeaderIcon}>
              <Calendar size={24} aria-hidden="true" />
            </div>
            <div>
              <h1 className={styles.linkTitle}>{vennLink.title}</h1>
              <p className={styles.linkWith}>with {businessName}</p>
            </div>
          </div>
          {vennLink.description && <p className={styles.linkDesc}>{vennLink.description}</p>}
          <div className={styles.duration}>
            <Clock size={16} aria-hidden="true" />
            <span>{vennLink.duration_minutes} minutes</span>
          </div>
          {vennLink.price_amount > 0 && (
            <div className={styles.priceBadge}>
              {formatPrice(vennLink.price_amount, vennLink.price_currency)}
            </div>
          )}
        </div>

        {/* Calendar card */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>Select a Day</h2>
          <p className={styles.timezoneHint}>
            Times shown in {new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' }).format(new Date()).split(', ').pop()}
          </p>

          {/* Conversion nudges — only when slots are available */}
          {nextAvailableSlot && (
            <div className={styles.nudgeBanner}>
              <div className={styles.nudgeNext}>
                <span className={styles.nudgeLabel}>Next available:</span>
                <button
                  className={styles.nudgeQuickBook}
                  onClick={() => handleDaySelect(new Date(nextAvailableSlot.start))}
                >
                  {formatNextAvailable(nextAvailableSlot.start)}
                </button>
              </div>
              {weekSummary && (
                <div className={styles.nudgeWeekSummary}>
                  {weekSummary.thisWeekCount > 0 && (
                    <span>{weekSummary.thisWeekCount} slot{weekSummary.thisWeekCount !== 1 ? 's' : ''} this week</span>
                  )}
                  {weekSummary.thisWeekCount > 0 && weekSummary.nextWeekCount > 0 && (
                    <span className={styles.nudgeSep}>·</span>
                  )}
                  {weekSummary.nextWeekCount > 0 && (
                    <span>{weekSummary.nextWeekCount} slot{weekSummary.nextWeekCount !== 1 ? 's' : ''} next week</span>
                  )}
                </div>
              )}
            </div>
          )}

          {availableSlots.length === 0 ? (
            <div className={styles.noSlots}>
              <div className={`${styles.stateIcon} ${styles.stateIconNeutral}`}>
                <Calendar size={32} aria-hidden="true" />
              </div>
              <p className={styles.noSlotsText}>No available times at the moment.</p>
              <p className={styles.noSlotsHint}>Please check back later or contact us directly.</p>
            </div>
          ) : (
            <MonthView
              currentDate={viewDate}
              onDateChange={setViewDate}
              availabilityMap={availabilityMap}
              onSlotClick={handleDaySelect}
            />
          )}
        </div>

        {/* Day slot picker — shown when a day is selected */}
        {selectedDate && (
          <div className={styles.card}>
            <div className={styles.dayPickerHeader}>
              <button
                className={styles.backBtn}
                onClick={() => { setSelectedDate(null); setSelectedSlot(null); }}
                aria-label="Back to calendar"
              >
                <ChevronLeft size={16} aria-hidden="true" />
                <span>Back</span>
              </button>
              <h2 className={styles.sectionTitle}>{selectedDayLabel}</h2>
            </div>
            {slotsForSelectedDate.length === 0 ? (
              <p className={styles.noSlotsHint}>No slots available for this day — please select another.</p>
            ) : (
              <div className={styles.slotList}>
                {slotsForSelectedDate.map((slot, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSelectedSlot(slot);
                      postToParent({ type: 'vennio:slot_selected', shortCode, start: slot.start, end: slot.end });
                    }}
                    className={`${styles.slotCard}${selectedSlot === slot ? ` ${styles.slotCardSelected}` : ''}`}
                  >
                    <span className={styles.slotCardTime}>{formatTime(slot.start)}</span>
                    <span className={styles.slotCardSep}>–</span>
                    <span className={styles.slotCardEnd}>{formatTime(slot.end)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Booking form — shown when a slot is selected */}
        {selectedSlot && (
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Your Information</h2>

            {error && (
              <div className={styles.errorBox}>
                <AlertCircle size={20} className={styles.errorIcon} aria-hidden="true" />
                <p className={styles.errorText}>{error}</p>
              </div>
            )}

            <form onSubmit={handleBooking} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="name" className={styles.label}>Full Name *</label>
                <input
                  type="text" id="name" required
                  value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                  className={styles.input} placeholder="John Doe"
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email Address *</label>
                <input
                  type="email" id="email" required
                  value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                  className={styles.input} placeholder="john@example.com"
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="timezone" className={styles.label}>
                  <Globe size={14} className={styles.labelIcon} aria-hidden="true" />
                  Timezone
                </label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className={styles.input}
                >
                  {/* Show detected timezone at top if it's not in the list */}
                  {!COMMON_TIMEZONES.includes(timezone) && (
                    <option value={timezone}>{tzLabel(timezone)}</option>
                  )}
                  {COMMON_TIMEZONES.map(tz => (
                    <option key={tz} value={tz}>{tzLabel(tz)}</option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label htmlFor="notes" className={styles.label}>Notes (Optional)</label>
                <textarea
                  id="notes" rows={3}
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  className={styles.input} placeholder="Anything we should know about this meeting?"
                />
              </div>

              {/* Calendar sharing prompt */}
              <div className={styles.calendarPrompt}>
                <Calendar size={24} className={styles.calendarPromptIcon} aria-hidden="true" />
                <div>
                  <h3 className={styles.calendarPromptTitle}>Get Better Meeting Times</h3>
                  <p className={styles.calendarPromptBody}>
                    Share your calendar to see times that work for both of you. We only see your busy/free times, not event details.
                  </p>
                  <a
                    href={`/access-request/${vennLink?.business_id}?returnTo=/book/${shortCode}`}
                    className={styles.calendarPromptLink}
                    target="_blank" rel="noopener noreferrer"
                  >
                    <LinkIcon size={16} aria-hidden="true" />
                    Connect Your Calendar
                  </a>
                </div>
              </div>

              {/* Selected time summary */}
              <div className={styles.selectedSummary}>
                <p className={styles.selectedLabel}>Selected Time:</p>
                <p className={styles.selectedDate}>{formatDate(selectedSlot.start)}</p>
                <p className={styles.selectedTime}>{formatTimeRange(selectedSlot.start, selectedSlot.end)}</p>
                {vennLink.price_amount > 0 && (
                  <p className={styles.selectedPrice}>
                    {formatPrice(vennLink.price_amount, vennLink.price_currency)}
                  </p>
                )}
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setSelectedSlot(null)} className={styles.changeBtn}>
                  Change Time
                </button>
                <button type="submit" disabled={isSubmitting} className={styles.confirmBtn}>
                  {isSubmitting
                    ? 'Booking...'
                    : vennLink.price_amount > 0
                      ? `Confirm & Pay ${formatPrice(vennLink.price_amount, vennLink.price_currency)}`
                      : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
        )}

        {!isEmbed && (
          <div className={styles.footer}>
            <p>Powered by <span className={styles.footerBrand}>Vennio</span></p>
          </div>
        )}
      </div>
    </div>
  );
}
